import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Check, ChevronDown, ChevronUp } from "lucide-react";
import CheckoutModal from "@/components/CheckoutModal";
import { API } from "@/lib/api";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left">
        <span className="text-sm font-semibold text-white pr-4">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-white/40 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0" />}
      </button>
      {open && <p className="text-white/50 text-sm pb-5 leading-relaxed">{a}</p>}
    </div>
  );
};

export default function ConversionPage({
  accentColor = "blue",
  badge,
  headline,
  subheadline,
  heroImage,
  painTitle = "Sound familiar?",
  painPoints = [],
  features = [],
  featureTitle = "What You Get",
  extraSection,
  testimonial,
  price,
  priceInterval,
  priceCta,
  priceTier,
  faqs = [],
  backPath = "/",
}) {
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const [refSlug, setRefSlug] = useState("");
  const [refCoupon, setRefCoupon] = useState("");

  const tierInfo = { name: badge?.split("|")[0]?.trim() || "Plan", priceDisplay: price, interval: priceInterval };

  // Look up affiliate from ?ref= param and auto-get their coupon
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setRefSlug(ref);
      fetch(`${API}/affiliates/lookup?ref=${ref}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.coupon_code) setRefCoupon(data.coupon_code);
        })
        .catch(() => {});
    }
  }, []);

  const colorMap = {
    blue: { accent: "text-blue-400", bg: "bg-blue-500", bgLight: "bg-blue-500/10", border: "border-blue-500/30", btn: "bg-blue-600 hover:bg-blue-500", dot: "bg-blue-400", ring: "ring-blue-500/20" },
    emerald: { accent: "text-emerald-400", bg: "bg-emerald-500", bgLight: "bg-emerald-500/10", border: "border-emerald-500/30", btn: "bg-emerald-600 hover:bg-emerald-500", dot: "bg-emerald-400", ring: "ring-emerald-500/20" },
    rose: { accent: "text-rose-400", bg: "bg-rose-500", bgLight: "bg-rose-500/10", border: "border-rose-500/30", btn: "bg-rose-600 hover:bg-rose-500", dot: "bg-rose-400", ring: "ring-rose-500/20" },
    amber: { accent: "text-amber-400", bg: "bg-amber-500", bgLight: "bg-amber-500/10", border: "border-amber-500/30", btn: "bg-amber-600 hover:bg-amber-500", dot: "bg-amber-400", ring: "ring-amber-500/20" },
  };
  const c = colorMap[accentColor] || colorMap.blue;

  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          {heroImage && (
            <img src={heroImage} alt="" className="w-full h-full object-cover opacity-20" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#070B14]/60 via-[#070B14]/80 to-[#070B14]" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 pt-12 pb-20">
          <button onClick={() => navigate(backPath)} className="flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mb-10 transition-colors" data-testid="conv-back">
            <ChevronRight className="w-4 h-4 rotate-180" /> Back
          </button>

          <div className="flex items-center gap-3 mb-2">
            <img src="/paladin-logo.jpeg" alt="Paladin" className="w-10 h-10 rounded-lg object-contain" />
            <span className="font-bold text-sm tracking-wider text-white/50">PALADIN</span>
          </div>

          {badge && (
            <p className={`text-xs tracking-[0.2em] uppercase ${c.accent} font-semibold mt-6 mb-3`}>{badge}</p>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-6 max-w-3xl"
          >
            {headline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="text-white/60 text-base sm:text-lg max-w-2xl leading-relaxed mb-10"
          >
            {subheadline}
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
            onClick={() => setShowCheckout(true)}
            className={`${c.btn} text-white font-semibold px-8 py-3.5 rounded-xl transition-colors text-sm tracking-wide flex items-center gap-2`}
            data-testid="hero-cta"
          >
            {priceCta || "Get Started"} <ChevronRight className="w-4 h-4" />
          </motion.button>
        </div>
      </section>

      {/* Pain Points */}
      {painPoints.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className={`text-xs tracking-[0.2em] uppercase ${c.accent} font-semibold mb-3`}>{painTitle}</p>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-10">We get it.</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {painPoints.map((point, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`${c.bgLight} border ${c.border} rounded-2xl p-6`}
              >
                <p className="text-white/80 text-sm leading-relaxed">{point}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Extra Section (custom content per page) */}
      {extraSection}

      {/* What You Get */}
      {features.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-10">{featureTitle}</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4 bg-[#0f172a] border border-[#1E293B] rounded-xl p-5"
              >
                <Check className={`w-5 h-5 ${c.accent} mt-0.5 flex-shrink-0`} />
                <div>
                  <p className="text-white font-semibold text-sm">{f.title}</p>
                  {f.desc && <p className="text-white/50 text-xs mt-1 leading-relaxed">{f.desc}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Social Proof */}
      {testimonial && (
        <section className="max-w-5xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className={`${c.bgLight} border ${c.border} rounded-2xl p-8 md:p-10 text-center`}
          >
            <p className="text-white/80 text-base sm:text-lg italic leading-relaxed mb-4">"{testimonial.quote}"</p>
            <p className={`text-sm font-semibold ${c.accent}`}>{testimonial.name}</p>
            {testimonial.detail && <p className="text-white/40 text-xs mt-1">{testimonial.detail}</p>}
          </motion.div>
        </section>
      )}

      {/* Pricing CTA */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-[#0f172a] border border-[#1E293B] rounded-2xl p-8 md:p-12 text-center"
        >
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-4">Ready to Start?</h2>
          <div className="flex items-baseline justify-center gap-2 mb-2">
            <span className="text-5xl font-black text-white">{price}</span>
            <span className="text-white/50 text-lg">/ {priceInterval}</span>
          </div>
          <p className="text-white/40 text-sm mb-8">Billed {priceInterval}ly. Cancel anytime.</p>
          <button
            onClick={() => setShowCheckout(true)}
            className={`${c.btn} text-white font-semibold px-10 py-4 rounded-xl transition-colors text-base tracking-wide`}
            data-testid="pricing-cta"
          >
            {priceCta || "Get Started"}
          </button>
        </motion.div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 py-16 pb-24">
          <h2 className="text-2xl font-black tracking-tight mb-8">Common Questions</h2>
          <div className="bg-[#0f172a] border border-[#1E293B] rounded-2xl px-6">
            {faqs.map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} />)}
          </div>
        </section>
      )}

      {/* Login */}
      <div className="text-center pb-12">
        <p className="text-white/40 text-xs">
          Already a member?{" "}
          <button onClick={() => navigate("/login")} className="text-white/70 hover:text-white underline underline-offset-2 transition-colors">
            Sign in
          </button>
        </p>
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        tier={priceTier}
        tierInfo={tierInfo}
        couponCode={refCoupon}
        referralSlug={refSlug}
      />
    </div>
  );
}
